// src/components/CardWithLogo.js
import React, { useRef, useState } from "react";
import CepInfoStep from "./steps/CepInfoStep";
import HospitalsListStep from "./steps/HospitalsListStep";
import PersonalInfoStep from "./steps/PersonalInfoStep";
import ProfessionalInfoStep from "./steps/ProfessionalInfoStep";
import PlanosInfoStep from "./steps/PlanosInfoStep";
import { fetchHospitalsNearby, createLead } from "../services/api";

const CardWithLogo = ({ currentStep, handleNext, handleBack }) => {
  const personalRef = useRef(null);
  const professionalRef = useRef(null);

  const [cep, setCep] = useState("");
  const [cepValid, setCepValid] = useState(false);
  const [hasHospitals, setHasHospitals] = useState(false);
  const [canProceedPersonal, setCanProceedPersonal] = useState(false);
  const [canProceedProfessional, setCanProceedProfessional] = useState(false);
  const [loading, setLoading] = useState(false);
  const totalSteps = 4;

  const onContinue = async () => {
    if (loading) return;

    if (currentStep === 1) {
      if (!cepValid || !hasHospitals) {
        alert("Informe um CEP válido e aguarde ao menos uma unidade.");
        return;
      }
      handleNext({ cep });
      return;
    }

    if (currentStep === 2) {
      const getPersonal = personalRef.current?.getData;
      if (typeof getPersonal === "function" && canProceedPersonal) {
        const personal = getPersonal();
        setLoading(true);
        try {
          const formData = {
            cep,
            name: personal.name,
            email: personal.email,
            phone: personal.phone,
            birth_date: personal.birth_date,
            city: personal.city,
            state: personal.state,
            zip_code: personal.zip_code,
            cpf_cnpj: personal.cpf_cnpj,
          };
          const res = await createLead(formData);
          localStorage.setItem("leadId", res.message.id);
          handleNext(personal);
        } catch (err) {
          console.error("Erro ao criar lead =>", err);
          const msg = err?.response?.data?.message || "";
          if (msg.includes("Duplicate entry")) {
            if (msg.includes("leads_email_unique")) {
              alert("Já existe uma solicitação para este email.");
            } else if (msg.includes("leads_cpf_cnpj_unique")) {
              alert("Já existe uma solicitação para este CPF/CNPJ.");
            } else {
              alert("Já existe uma solicitação com estes dados.");
            }
          } else {
            alert("Ops! Erro ao enviar seus dados. Tente novamente mais tarde.");
          }
        } finally {
          setLoading(false);
        }
      }
      return;
    }

    if (currentStep === 3) {
      const getProfessional = professionalRef.current?.getData;
      if (typeof getProfessional === "function" && canProceedProfessional) {
        handleNext(getProfessional());
      }
      return;
    }

    if (currentStep === 4) {
      handleNext({});
      return;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <CepInfoStep
              setCep={setCep}
              cepValid={cepValid}
              setCepValid={setCepValid}
            />
            {cepValid && (
              <HospitalsListStep cep={cep} onCount={setHasHospitals} />
            )}
          </>
        );
      case 2:
        return (
          <PersonalInfoStep
            ref={personalRef}
            setCanProceed={setCanProceedPersonal}
          />
        );
      case 3:
        return (
          <ProfessionalInfoStep
            ref={professionalRef}
            setCanProceed={setCanProceedProfessional}
          />
        );
      case 4:
        return <PlanosInfoStep />;
      default:
        return null;
    }
  };

  return (
    <div
      className={`
        w-full
        ${currentStep === 4 ? "max-w-[98%]" : "max-w-[520px]"}
        flex flex-col items-center
      `}
    >
      {/* Etapa */}
      <div className="mb-4">
        <span className="text-lg font-medium text-[#4B5563]">
          Etapa {currentStep} de {totalSteps}
        </span>
      </div>

      {/* Círculos + linhas */}
      <div className="flex items-center mb-8">
        {[1, 2, 3, 4].map((step, idx) => (
          <React.Fragment key={step}>
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                step < currentStep
                  ? "bg-[#FFC700]"
                  : step === currentStep
                  ? "bg-[#2A4DD0]"
                  : "bg-[#C0BEC1]"
              }`}
            >
              {step}
            </div>
            {idx < totalSteps - 1 && (
              <div
                className={`h-1 w-12 ${
                  step < currentStep ? "bg-[#FFC700]" : "bg-[#C0BEC1]"
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Headers por etapa */}
      {currentStep === 1 && (
        <>
          <h1 className="text-[24px] sm:text-[26px] font-bold text-[#224189] text-center leading-tight">
            Encontre o melhor <br /> plano de saúde para você
          </h1>
          <p className="mt-1 text-base text-[#4B5563] text-center">
            Para começar, insira seu CEP abaixo:
          </p>
        </>
      )}
      {currentStep === 2 && (
        <h2 className="text-lg sm:text-xl font-medium text-[#4B5563] text-center mb-6">
          Agora insira suas informações pessoais
        </h2>
      )}
      {currentStep === 3 && (
        <h2 className="text-lg sm:text-xl font-medium text-[#4B5563] text-center mb-6">
          Para finalizar, informe seus dados profissionais:
        </h2>
      )}
      {currentStep === 4 && (
        <h2 className="text-lg sm:text-xl font-medium text-[#4B5563] text-center mb-6">
          Escolha seu plano
        </h2>
      )}

      {/* Conteúdo do step */}
      <div className="w-full mt-6">{renderStep()}</div>

      {/* Botões (só para steps 1-3) */}
      {currentStep < 4 && (
        <div className="flex justify-between items-center w-full mt-16">
          <button
            onClick={() =>
              currentStep === 1
                ? (window.location.href = "http://alleanzabrasilcorretora.com.br/")
                : handleBack()
            }
            className="h-12 px-4 border border-[#2A4DD0] rounded-md text-[#2A4DD0] font-medium text-sm"
          >
            ‹ Voltar
          </button>
          <button
            onClick={onContinue}
            disabled={
              loading ||
              (currentStep === 1 && (!cepValid || !hasHospitals)) ||
              (currentStep === 2 && !canProceedPersonal) ||
              (currentStep === 3 && !canProceedProfessional)
            }
            className={`h-12 px-6 rounded-md text-white font-medium text-sm ${
              loading ||
              (currentStep === 1 && (!cepValid || !hasHospitals)) ||
              (currentStep === 2 && !canProceedPersonal) ||
              (currentStep === 3 && !canProceedProfessional)
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#2A4DD0] hover:bg-blue-700"
            }`}
          >
            {loading
              ? "Processando..."
              : currentStep === 2
              ? "Enviar Dados ›"
              : "Continuar ›"}
          </button>
        </div>
      )}
    </div>
  );
};

export default CardWithLogo;
